import type { AiAction } from '~/components/AiChat/types';
import type { StpNotification } from '~/components/ClaimPage/CliveApp/CliveNotifications/types';

import type { ConductorAsk } from '../types';
import { activityRowKey, getDrawnAskEntries, resolveAskState } from '../utils';

import { buildRecommendationRows } from './recommendationRows';
import type { ActivityRow, QuestionRow, RequestRow } from './types';
import { raisedAt } from './utils';

/**
 * An ask's row. Its state is read off the ask, which is the authority — a handler answering or
 * rejecting one publishes no entry — and its card opens the entry the log draws the ask at.
 */
const askRow = (ask: ConductorAsk, drawn: Map<string, StpNotification>): RequestRow | QuestionRow => {
  const shared = {
    key: activityRowKey(ask.kind, ask.id),
    state: resolveAskState(ask),
    raisedAt: raisedAt(ask.created_at),
    logEventId: drawn.get(ask.id)?.event_id,
  };

  return ask.kind === 'request' ? { kind: 'request', ask, ...shared } : { kind: 'question', ask, ...shared };
};

/**
 * Everything Clive raised on the claim — its recommendations and its asks — each in its resolved
 * state, interleaved in the order it raised them. The panel's filters decide which of them it shows.
 *
 * Given the notifications in the order the log draws them, so an ask's card opens the same entry
 * the log drew it at.
 */
export const buildActivityRows = (
  actions: AiAction[],
  asks: ConductorAsk[],
  notifications: StpNotification[]
): ActivityRow[] => {
  const drawn = getDrawnAskEntries(notifications);

  return [...buildRecommendationRows(actions, notifications), ...asks.map((ask) => askRow(ask, drawn))].sort(
    (first, second) => first.raisedAt - second.raisedAt
  );
};
